import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiError, API_BASE } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength } from "@/components/PasswordStrength";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Power, Settings2, Loader2, RotateCcw, Trash2 } from "lucide-react";

const FEATURES = [
  ["visitor_management", "Visitor Management"], ["meeting_room_booking", "Meeting Room Booking"],
  ["reports", "Reports"], ["audit_logs", "Audit Logs"], ["wall_display", "Wall Display"],
  ["visitor_history", "Visitor History"], ["visitor_kiosk", "Visitor Kiosk"],
];

export default function Companies() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [feat, setFeat] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [resetCo, setResetCo] = useState(null);
  const [delCo, setDelCo] = useState(null);
  const [confirmTxt, setConfirmTxt] = useState("");
  const [busy, setBusy] = useState(false);

  const doReset = async () => {
    setBusy(true);
    try { await api.post(`/companies/${resetCo.id}/reset`); toast.success("Company data reset (admin login kept)"); setResetCo(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/companies/${delCo.id}`); toast.success("Company deleted"); setDelCo(null); setConfirmTxt(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const load = () => api.get("/companies").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ status: "active" }); setLogoFile(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setLogoFile(null); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      let cid;
      if (editing) { await api.put(`/companies/${editing.id}`, form); cid = editing.id; toast.success("Company updated"); }
      else { const { data } = await api.post("/companies", form); cid = data.company.id; toast.success(`Company created. Company Admin can log in as ${form.admin_email}`); }
      if (logoFile && cid) {
        const fd = new FormData(); fd.append("file", logoFile);
        await api.post(`/companies/${cid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setOpen(false); setLogoFile(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (c) => { try { await api.patch(`/companies/${c.id}/status`); load(); } catch (e) { toast.error(apiError(e)); } };

  const saveFeatures = async () => {
    try { await api.put(`/companies/${feat.id}/features`, feat.features); toast.success("Features updated"); setFeat(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!list) return <Loader />;

  return (
    <div>
      <PageHeader title="Companies" subtitle="Manage tenant organizations and their enabled modules."
        actions={<Button onClick={openNew} data-testid="add-company-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1.5" /> New Company</Button>} />

      {list.length === 0 ? (
        <EmptyState icon={Building2} title="No companies yet" subtitle="Create your first tenant company to get started." action={<Button onClick={openNew} className="bg-navy-900">New Company</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow sf-fade-up" data-testid={`company-card-${c.code}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-navy-50 text-navy-800 flex items-center justify-center"><Building2 size={20} /></div>
                <StatusBadge status={c.status} />
              </div>
              <h3 className="font-display font-bold text-navy-900 mt-3 text-lg">{c.name}</h3>
              <p className="text-xs text-slate-400 font-mono">{c.code} · {c.branch_count} branches</p>
              <p className="text-sm text-slate-500 mt-2">{c.contact_person || "—"} · {c.phone || "—"}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" className="bg-navy-900 hover:bg-navy-800" onClick={() => navigate(`/companies/${c.id}`)} data-testid={`view-company-${c.code}`}>View</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-company-${c.code}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => setFeat({ ...c, features: { ...c.features } })} data-testid={`features-company-${c.code}`} title="Features"><Settings2 size={15} /></Button>
                <Button size="sm" variant="outline" onClick={() => toggle(c)} data-testid={`toggle-company-${c.code}`} title="Activate / Deactivate"><Power size={15} /></Button>
                <Button size="sm" variant="outline" onClick={() => setResetCo(c)} data-testid={`reset-company-${c.code}`} title="Reset company data" className="border-amber-200 text-amber-700 hover:bg-amber-50"><RotateCcw size={15} /></Button>
                <Button size="sm" variant="outline" onClick={() => { setDelCo(c); setConfirmTxt(""); }} data-testid={`delete-company-${c.code}`} title="Delete company" className="border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Company" : "New Company"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Company Name *"], ["code", "Company Code *"], ["contact_person", "Contact Person"], ["phone", "Phone Number"], ["email", "Email"], ["address", "Address"]].map(([k, l]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`company-${k}`} className="mt-1 h-10" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div>
              <Label>Company Logo <span className="text-slate-400 font-normal">(white-label branding)</span></Label>
              <Input type="file" accept="image/*" data-testid="company-logo-input" className="mt-1" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              {logoFile ? (
                <p className="text-xs text-emerald-600 mt-1">Selected: {logoFile.name}</p>
              ) : editing?.logo_url ? (
                <img src={`${API_BASE.replace(/\/api$/, "")}${editing.logo_url}`} alt="Current logo" className="h-10 mt-2 object-contain" data-testid="company-current-logo" />
              ) : null}
              <p className="text-[11px] text-slate-400 mt-1">Shown in the app header and on visitor passes & booking pages. PNG/JPG/WebP, under 3MB.</p>
            </div>
            {!editing && (
              <div className="pt-3 mt-2 border-t border-slate-100 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">Company Admin Login</p>
                <p className="text-xs text-slate-400 -mt-1">This admin will sign in and create branches, branch admins and receptionists.</p>
                <div><Label>Admin Name *</Label><Input data-testid="company-admin_name" className="mt-1 h-10" value={form.admin_name || ""} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} /></div>
                <div><Label>Admin Email *</Label><Input type="email" data-testid="company-admin_email" className="mt-1 h-10" value={form.admin_email || ""} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></div>
                <div><Label>Admin Password *</Label><PasswordInput testId="company-admin_password" className="mt-1 h-10" value={form.admin_password || ""} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} /><PasswordStrength value={form.admin_password} testId="company-admin_password-strength" /></div>
                <div><Label>Visitor Management Method *</Label>
                  <Select value={form.visitor_method || "email"} onValueChange={(v) => setForm({ ...form, visitor_method: v })}>
                    <SelectTrigger className="mt-1 h-10" data-testid="company-visitor-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Method 01 — Host Employee Email</SelectItem>
                      <SelectItem value="cabin_code">Method 02 — Company Cabin Code</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400 mt-1">Applied to all branches of this company (branch can override).</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-company-btn" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!feat} onOpenChange={(v) => !v && setFeat(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Feature Controls · {feat?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {FEATURES.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm font-medium text-navy-900">{l}</span>
                <Switch checked={feat?.features?.[k] !== false} data-testid={`feature-${k}`} onCheckedChange={(v) => setFeat({ ...feat, features: { ...feat.features, [k]: v } })} />
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={saveFeatures} data-testid="save-features-btn" className="bg-navy-900">Save Features</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetCo} onOpenChange={(o) => !o && setResetCo(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Company Data</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This permanently deletes all <span className="font-medium text-navy-900">{resetCo?.name}</span> branches, rooms, bookings, visitors and staff users. The <b>Company Admin login is kept</b> so they can start fresh. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetCo(null)}>Cancel</Button>
            <Button onClick={doReset} disabled={busy} data-testid="confirm-reset-company-btn" className="bg-amber-600 hover:bg-amber-700">{busy ? <Loader2 className="animate-spin" size={16} /> : "Reset Data"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delCo} onOpenChange={(o) => { if (!o) { setDelCo(null); setConfirmTxt(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Company</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This permanently deletes <span className="font-medium text-navy-900">{delCo?.name}</span> and <b>everything</b> in it — branches, rooms, bookings, visitors, and all users including the Company Admin. This cannot be undone.</p>
          <div className="mt-2">
            <Label className="text-sm">Type the company code <span className="font-mono font-semibold text-navy-900">{delCo?.code}</span> to confirm</Label>
            <Input className="mt-1.5 h-10" value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} data-testid="delete-company-confirm-input" placeholder={delCo?.code} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDelCo(null); setConfirmTxt(""); }}>Cancel</Button>
            <Button onClick={doDelete} disabled={busy || confirmTxt !== delCo?.code} data-testid="confirm-delete-company-btn" className="bg-rose-600 hover:bg-rose-700">{busy ? <Loader2 className="animate-spin" size={16} /> : "Delete Company"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
